
import React, { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { moduleRegistry } from "../lib/moduleRegistry";
import Scorecard from "../components/modules/Scorecard";
import AIRadar from "../components/modules/AIRadar";
import AILeaderboard from "../components/modules/AILeaderboard";
import AIMarketMap from "../components/modules/AIMarketMap";
import AIAnalyst from "../components/modules/AIAnalyst";
import AIVisibilityBreakdown from "../components/modules/AIVisibilityBreakdown";
import { colors } from "../lib/designTokens";
import Tooltip from "../components/Tooltip";
import OpportunityEngine from "../components/modules/OpportunityEngine";
import LocalVisibility from "../components/modules/LocalVisibility";
import LocalOpportunityEngine from "../components/modules/LocalOpportunityEngine";
import CaiCategoryWorkspace from "../components/modules/CaiCategoryWorkspace";
import { CaiGenerationEntitlementBoundary } from "../components/opportunities/CaiGenerationEntitlementContext";
import { isCaiGenerationEnabled } from "../lib/currentAssetIntelligence/caiGenerationEntitlement";
import { AIRetailVisibility } from "../components/modules/AIRetailVisibility";
import { isRetailVisibilityEnabled } from "../lib/retailVisibilityConfig";
import LocalHistoricalVisibility, { type LocalHistoryComparisonUpdate } from "../components/modules/LocalHistoricalVisibility";
import { getLocalRunCompletionLabel, getLocalRunFreshnessLabel } from "../lib/localRunFreshness";
import LocalReportingPeriodSelector from "../components/LocalReportingPeriodSelector";
import type { RunHistoryItem } from "../lib/runHistory";
import { getLocalReportingPeriod } from "../lib/localReportingPeriods";
import CompetitiveHistoricalVisibility from "../components/modules/CompetitiveHistoricalVisibility";
import AIConsensus from "../components/modules/AIConsensus";
import { buildRetailVisibilitySummary } from "../lib/buildRetailVisibilitySummary";
import {
  aggregateOrganicBrandMentionsForFrequency,
  normalizeBrandMentionKey
} from "../lib/brandMentionAggregation";
import { canReviewEntityGovernanceForRole } from "../lib/entityGovernanceAccess";
import {
  getDashboardAccountMode,
  isDashboardAccountEligible
} from "../lib/dashboardAccountEligibility";
import { getBusinessName as getAccountBusinessName } from "../lib/accountBusinessName";
import CustomerCurrentAssetIntelligence from "../components/modules/CustomerCurrentAssetIntelligence";
import { exportDashboardPdf } from "../utils/exportDashboardPdf";
import { postRunAnalysis } from "../lib/runAnalysisApi";
import { formatPercentage } from "../lib/percentageFormatting";

const componentMap: any = {
  radar: AIRadar,
  leaderboard: AILeaderboard,
  aiConsensus: AIConsensus,
  marketMap: AIMarketMap,
  opportunityEngine: OpportunityEngine,
  analyst: AIAnalyst,
  visibilityBreakdown: AIVisibilityBreakdown
};

type DashboardStatus = "loading" | "ready" | "empty" | "error";
type OeWorkspaceTab = "assets" | "entity" | "competitive";
type DashboardMetrics = {
  visibility: number;
  organicShare: number;
  recommendation: number;
  rank: number;
  totalCompetitors: number;
  authority: number;
};
type ScorecardMovement = "improved" | "declined" | "flat" | "unavailable";

const oeWorkspaceTabs: Array<{ id: OeWorkspaceTab; label: string }> = [
  { id: "assets", label: "Assets" },
  { id: "entity", label: "Entity" },
  { id: "competitive", label: "Competitive" }
];

function OeWorkspaceTabButton({
  active,
  id,
  label,
  onSelect
}: {
  active: boolean;
  id: OeWorkspaceTab;
  label: string;
  onSelect: (id: OeWorkspaceTab) => void;
}) {
  return <button
    id={`oe-workspace-tab-${id}`}
    role="tab"
    type="button"
    aria-selected={active}
    aria-controls={`oe-workspace-panel-${id}`}
    onClick={() => onSelect(id)}
    className="toren-tab"
  >{label}</button>;
}

function calculateDashboardMetrics(promptRows: any[], customerBrand: string): DashboardMetrics | null {
  const rollup = aggregateOrganicBrandMentionsForFrequency(
    promptRows,
    (row: any) => row.brand_mentions,
    customerBrand
  );
  const totalMentions = rollup.reduce((sum, item) => sum + item.count, 0);
  const metricsData = rollup
    .slice()
    .sort((a, b) => b.count - a.count)
    .map((item, index) => ({
      canonicalKey: item.canonicalKey,
      rawNames: item.rawNames,
      rank: index + 1,
      shareOfVoice: totalMentions > 0 ? item.count / totalMentions : 0
    }));

  if (!metricsData.length) return null;

  const normalizedCustomerBrand = normalizeBrandMentionKey(customerBrand);
  const myIndex = metricsData.findIndex((row) => (
    row.canonicalKey === normalizedCustomerBrand ||
    row.rawNames?.some((name: string) => normalizeBrandMentionKey(name) === normalizedCustomerBrand)
  ));
  const myRow = myIndex >= 0 ? metricsData[myIndex] : null;
  const visibility = myRow
    ? Number(myRow.shareOfVoice || 0) * 100
    : 0;

  return {
    visibility,
    organicShare: 0,
    recommendation: visibility,
    rank: myIndex >= 0 ? myIndex + 1 : 0,
    totalCompetitors: metricsData.length,
    authority: visibility > 0 ? Math.min(100, visibility * 1.15) : 0
  };
}

function movementFor(current: number, previous: number, lowerIsBetter = false): ScorecardMovement {
  if (current === previous) return "flat";
  const improved = lowerIsBetter ? current < previous : current > previous;
  return improved ? "improved" : "declined";
}

function percentageComparison(
  current: number,
  previous: number | null,
  subject: string
) {
  if (previous === null) return { movement: "unavailable" as const };
  const movement = movementFor(current, previous);
  const implication = movement === "improved"
    ? `${subject} increased since the previous run.`
    : movement === "declined"
      ? `${subject} decreased since the previous run.`
      : `${subject} held steady since the previous run.`;
  return { previousValue: formatPercentage(previous), movement, implication };
}

function positionComparison(current: DashboardMetrics, previous: DashboardMetrics | null) {
  if (!previous) return { movement: "unavailable" as const };
  const previousValue = previous.rank > 0
    ? `#${previous.rank} of ${previous.totalCompetitors}`
    : `outside top ${previous.totalCompetitors}`;
  const movement = current.rank <= 0 && previous.rank <= 0
    ? "flat"
    : current.rank > 0 && previous.rank <= 0
      ? "improved"
      : current.rank <= 0 && previous.rank > 0
        ? "declined"
        : movementFor(current.rank, previous.rank, true);
  const implication = movement === "improved"
    ? "Competitive position improved since the previous run."
    : movement === "declined"
      ? "Competitive position declined since the previous run."
      : "Competitive position held steady since the previous run.";
  return { previousValue, movement, implication };
}

function EntityFoundationCard({ accountName }: { accountName: string }) {
  return <section data-testid="dashboard-entity-foundation" aria-labelledby="dashboard-entity-foundation-heading" style={{ marginTop: 18, border: "1px solid #dbeafe", borderRadius: 14, padding: 16, background: "#ffffff" }}>
    <div style={{ color: "#2563eb", fontSize: 12, fontWeight: 900, letterSpacing: "0.06em", textTransform: "uppercase" }}>Entity foundation</div>
    <h2 id="dashboard-entity-foundation-heading" style={{ margin: "6px 0 0", color: "#172554", fontSize: 21 }}>A consistent understanding of {accountName || "this business"}</h2>
    <p style={{ margin: "9px 0 0", color: "#475569", lineHeight: 1.5 }}>AI systems need a consistent understanding of the business and entity. Entity clarity supports visibility and recommendation strength.</p>
    <p style={{ margin: "8px 0 0", color: "#64748b", fontSize: 13, lineHeight: 1.45 }}>Review available entity recommendations below. Evidence review does not approve an entity mapping; existing authorization and governance still apply.</p>
  </section>;
}

export default function Dashboard() {
  const [accounts, setAccounts] = useState<any[]>([]);
  const [isPlatformAdminForCai, setIsPlatformAdminForCai] = useState(false);
  const [account, setAccount] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [latestRunDate, setLatestRunDate] = useState<string | null>(null);
  const [latestRunUpdateContext, setLatestRunUpdateContext] = useState<{ accountId: string; status: string | null } | null>(null);
  const [promptDataset, setPromptDataset] = useState<any[]>([]);
  const [latestRunContext, setLatestRunContext] = useState<{ accountId: string; runId: string } | null>(null);
  const [localRunsContext, setLocalRunsContext] = useState<{ accountId: string; runs: RunHistoryItem[] } | null>(null);
  const [selectedLocalRunContext, setSelectedLocalRunContext] = useState<{ accountId: string; runId: string } | null>(null);
  const [localHistoryComparison, setLocalHistoryComparison] = useState<LocalHistoryComparisonUpdate | null>(null);
  const [previousRunId, setPreviousRunId] = useState<string | null>(null);
  const [dashboardStatus, setDashboardStatus] = useState<DashboardStatus>("loading");
  const [dashboardMessage, setDashboardMessage] = useState("");
  const [oeWorkspaceTab, setOeWorkspaceTab] = useState<OeWorkspaceTab>("assets");
  const [metricsStatus, setMetricsStatus] = useState<"ready" | "empty" | "error">("empty");
  const [retailVisibilitySummary, setRetailVisibilitySummary] = useState(
  buildRetailVisibilitySummary([])
);
  const [metrics, setMetrics] = useState<DashboardMetrics>({
  visibility: 0,
  organicShare: 0,
  recommendation: 0,
  rank: 0,
  totalCompetitors: 0,
  authority: 0
});
  const [previousMetrics, setPreviousMetrics] = useState<DashboardMetrics | null>(null);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const dashboardExportRef = useRef<HTMLDivElement | null>(null);

  const modules = Object.entries(moduleRegistry);

const selectedAccount = accounts.find((a: any) => a.account_id === account);
const customerBrand = getBusinessName(selectedAccount);
const accountRef = useRef<string | null>(account);
accountRef.current = account;
const latestRunId = latestRunContext?.accountId === account ? latestRunContext.runId : null;
const activeLatestRunDate = latestRunContext?.accountId === account ? latestRunDate : null;
const latestRunUpdateStatus = selectedAccount?.run_status === "running"
  ? "running"
  : latestRunUpdateContext?.accountId === account ? latestRunUpdateContext.status : null;

const selectedRole = String(selectedAccount?.user_role || "").toLowerCase();

type AccountLike = {
  account_name?: string | null;
  display_name?: string | null;
  business_name?: string | null;
  local_config?: { business_name?: string | null } | null;
  market?: string | null;
};

async function fetchRetailVisibilitySummary(runId: string | null, requestedAccount: string | null = account) {
  if (!requestedAccount || !runId || !isRetailVisibilityEnabled(requestedAccount)) {
    setRetailVisibilitySummary(buildRetailVisibilitySummary([]));
    return;
  }
  const { data: retailResultsData, error: retailResultsError } = await supabase
    .from("prompt_results")
    .select(`
      category,
      prompt_score,
      visibility_signal,
      brand_mentions,
      llm_response_text,
      prompt_type
    `)
    .eq("account_id", requestedAccount)
    .eq("run_id", runId)
    .like("category", "retail_%");

  if (retailResultsError) {
    console.error("Retail visibility results error:", retailResultsError);
    if (accountRef.current !== requestedAccount) return;
    setRetailVisibilitySummary(buildRetailVisibilitySummary([]));
    return;
  }

  if (accountRef.current !== requestedAccount) return;
  setRetailVisibilitySummary(
    buildRetailVisibilitySummary(retailResultsData ?? [])
  );
}

function getAccountDisplayName(account: AccountLike | null | undefined): string {
  if (!account) return "No account selected";

  return (
    account.display_name ||
    account.account_name ||
    account.business_name ||
    "No account selected"
  );
}

function getBusinessName(account: AccountLike | null | undefined): string {
  return getAccountBusinessName(account);
}

const rolesWithOpportunityEngine = [
  "super_admin",
  "admin",
  "sales_partner",
  "agency",
  "customer"
];

const canViewOpportunityEngine =
  rolesWithOpportunityEngine.includes(selectedRole) ||
  Boolean(selectedAccount?.can_view_opportunity_engine);

const accountType = String(selectedAccount?.account_type || "competitive").toLowerCase();
const accountMode = getDashboardAccountMode(selectedAccount);

const isLocalAccount = accountMode === "local";

const activeLocalRuns = localRunsContext?.accountId === account ? localRunsContext.runs : [];
const selectedLocalRun = isLocalAccount
  ? getLocalReportingPeriod(
      activeLocalRuns,
      selectedLocalRunContext?.accountId === account ? selectedLocalRunContext.runId : null
    )
  : null;
const activeLocalRunId = isLocalAccount ? selectedLocalRun?.run_id || latestRunId : latestRunId;
const activeRunFreshnessStatus = activeLocalRunId === latestRunId ? latestRunUpdateStatus : null;
const activeRunFreshnessLabel = isLocalAccount
  ? getLocalRunCompletionLabel(selectedLocalRun, new Date(), activeRunFreshnessStatus)
  : getLocalRunFreshnessLabel(activeLatestRunDate, new Date(), latestRunUpdateStatus);

const handleLocalHistoryComparisonUpdate = useCallback((comparison: LocalHistoryComparisonUpdate) => {
  if (comparison.accountId !== account || comparison.currentRunId !== activeLocalRunId) return;
  setLocalHistoryComparison(comparison);
}, [account, activeLocalRunId]);

const activeLocalHistoryComparison = localHistoryComparison?.accountId === account && localHistoryComparison.currentRunId === activeLocalRunId
  ? localHistoryComparison
  : null;

function handleLocalReportingPeriodChange(runId: string) {
  if (!account || !activeLocalRuns.some((run) => run.run_id === runId)) return;
  setSelectedLocalRunContext({ accountId: account, runId });
  setLocalHistoryComparison(null);
}

const canRunAnalysis =
  selectedRole === "super_admin" || selectedRole === "admin";

const canReviewEntityGovernance = canReviewEntityGovernanceForRole(selectedRole);

const canSwitchAccounts = accounts.length > 1;

async function handleExportPdf() {
  if (!dashboardExportRef.current || isExportingPdf) return;

  setIsExportingPdf(true);

  try {
    await exportDashboardPdf({
      element: dashboardExportRef.current,
      accountName: getAccountDisplayName(selectedAccount)
    });
  } catch (error) {
    console.error("[Dashboard] PDF export failed:", error);
    window.alert("Toren couldn't generate the PDF. Please try again.");
  } finally {
    setIsExportingPdf(false);
  }
}

  // ----------------------------
  // INITIAL LOAD
  // ----------------------------
  useEffect(() => {
    fetchAccounts();
  }, []);

  // ----------------------------
  // LOAD RUN ID
  // ----------------------------
  useEffect(() => {
  if (!account) return;

  setLatestRunContext(null);
  setPreviousRunId(null);
  setLatestRunDate(null);
  setLocalRunsContext(null);
  setSelectedLocalRunContext(null);
  setLocalHistoryComparison(null);
  setPromptDataset([]);
  setMetricsStatus("empty");
  setMetrics({
    visibility: 0,
    organicShare: 0,
    recommendation: 0,
    rank: 0,
    totalCompetitors: 0,
    authority: 0
  });
  setPreviousMetrics(null);

  fetchLatestRunId(account);
  if (!isLocalAccount) fetchLatestRunDate(account);
  // Query handlers are scoped to the selected account; rerun this effect only when that account changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [account]);
  // ----------------------------
  // LOAD DATA AFTER RUN ID
  // ----------------------------
  useEffect(() => {
    if (!account || !latestRunId) return;
    fetchDataset();
    fetchPromptDataset();
  }, [account, latestRunId, previousRunId]);

  // ----------------------------
  // FETCH ACCOUNTS
  // ----------------------------
  async function fetchAccounts() {
  setDashboardStatus("loading");
  setDashboardMessage("");

  try {
    const {
      data: { user },
      error: userError
    } = await supabase.auth.getUser();

    if (userError || !user) {
      console.error("[Dashboard] User error:", userError);
      setAccounts([]);
      setAccount(null);
      setIsPlatformAdminForCai(false);
      setDashboardStatus("error");
      setDashboardMessage("Please sign in to view your dashboard.");
      return;
    }

   const { data: accessRows, error: accessError } = await supabase
  .from("account_users")
  .select("account_id, role, can_view_opportunity_engine, is_active")
  .eq("user_id", user.id);

    if (accessError) {
      console.error("[Dashboard] Account access error:", accessError);
      setAccounts([]);
      setAccount(null);
      setIsPlatformAdminForCai(false);
      setDashboardStatus("error");
      setDashboardMessage("Your account access could not be loaded.");
      return;
    }

    if (!accessRows?.length) {
      setAccounts([]);
      setAccount(null);
      setIsPlatformAdminForCai(false);
      setDashboardStatus("empty");
      setDashboardMessage("No accounts are available for this user.");
      return;
    }

    // This only controls action visibility; the recovery API independently
    // verifies the same platform-admin role with the service-side auth helper.
    setIsPlatformAdminForCai(accessRows.some((row: any) => row.is_active === true
      && ["super_admin", "superadmin"].includes(String(row.role || "").toLowerCase())));

    const opportunityEngineByAccount = new Map(
  accessRows.map((row: any) => [
    row.account_id,
    Boolean(row.can_view_opportunity_engine)
  ])
);
const roleByAccount = new Map(
  accessRows.map((row: any) => [
    row.account_id,
    String(row.role || "").toLowerCase()
  ])
);

    const accessibleAccountIds = accessRows.map((row: any) => row.account_id);

    const { data: accountRows, error: accountError } = await supabase
  .from("accounts")
  .select(`
    account_id,
    account_name,
    display_name,
    business_name,
    market,
    analyst_pro_enabled,
    status,
    account_type,
    run_status,
    parent_account_id,
    local_config
  `)
  .in("account_id", accessibleAccountIds)
  .in("status", ["active", "demo"])
  .order("account_name");

    if (accountError) {
      console.error("[Dashboard] Accounts error:", accountError);
      setAccounts([]);
      setAccount(null);
      setIsPlatformAdminForCai(false);
      setDashboardStatus("error");
      setDashboardMessage("Your accounts could not be loaded.");
      return;
    }

    const visibleAccounts = (accountRows || [])
  .map((account: any) => ({
    ...account,
    user_role: roleByAccount.get(account.account_id),
    can_view_opportunity_engine:
      opportunityEngineByAccount.get(account.account_id) || false
  }))
      .filter((account: any) => {
        return isDashboardAccountEligible(account, account.user_role);
      });

    if (!visibleAccounts.length) {
      setAccounts([]);
      setAccount(null);
      setIsPlatformAdminForCai(false);
      setDashboardStatus("empty");
      setDashboardMessage("No accounts are available for this user.");
      return;
    }

    setAccounts(visibleAccounts);
    setAccount(visibleAccounts[0].account_id);
    setDashboardStatus("ready");
    setDashboardMessage("");
  } catch (err) {
    console.error("[Dashboard] fetchAccounts fatal error:", err);
    setAccounts([]);
    setIsPlatformAdminForCai(false);
    setAccount(null);
    setDashboardStatus("error");
    setDashboardMessage("The dashboard is not available right now.");
  }
}

  // ----------------------------
  // FETCH LATEST RUN ID
  // ----------------------------
  async function fetchLatestRunDate(requestedAccount: string | null = account) {
  if (!requestedAccount) return;

  const { data, error } = await supabase
    .from("runs")
    .select("run_date")
    .eq("account_id", requestedAccount)
    .eq("status", "completed")
    .order("run_date", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error("[Dashboard] Run date query error", error);
    if (accountRef.current === requestedAccount) setLatestRunDate(null);
    return;
  }

  if (accountRef.current !== requestedAccount) return;
  setLatestRunDate(data?.run_date || null);
}
  async function fetchLatestRunId(requestedAccount: string | null = account) {
  if (!requestedAccount) {
    setLatestRunContext(null);
    setLatestRunUpdateContext(null);
    setLocalRunsContext(null);
    setSelectedLocalRunContext(null);
    setLocalHistoryComparison(null);
    if (isLocalAccount) setLatestRunDate(null);
    return;
  }

  setLatestRunUpdateContext({ accountId: requestedAccount, status: null });
  const [completedRunsResult, latestUpdateResult] = await Promise.all([
    supabase
      .from("runs")
      .select("run_id, run_date, completed_at, started_at, run_label, environment, status, prompt_count")
      .eq("account_id", requestedAccount)
      .eq("status", "completed")
      .order("completed_at", { ascending: false, nullsFirst: false })
      .order("run_date", { ascending: false, nullsFirst: false })
      .limit(isLocalAccount ? 12 : 2),
    supabase
      .from("runs")
      .select("status, created_at")
      .eq("account_id", requestedAccount)
      .order("created_at", { ascending: false, nullsFirst: false })
      .limit(1)
      .maybeSingle()
  ]);
  const { data, error } = completedRunsResult;

  if (error) {
    console.error("[Dashboard] Run ID query error", error);
    if (accountRef.current === requestedAccount) {
      setLatestRunContext(null);
      setLocalRunsContext(isLocalAccount ? { accountId: requestedAccount, runs: [] } : null);
      if (isLocalAccount) setLatestRunDate(null);
    }
    return;
  }

  const completedRuns = (data || []) as RunHistoryItem[];
  const latestRun = completedRuns[0] || null;
  const resolvedLatestRunId = latestRun?.run_id || null;
  if (accountRef.current !== requestedAccount) return;
  setLatestRunUpdateContext({ accountId: requestedAccount, status: latestUpdateResult.error ? null : latestUpdateResult.data?.status || null });
  setPreviousRunId(completedRuns[1]?.run_id || null);
  setLatestRunContext(resolvedLatestRunId ? { accountId: requestedAccount, runId: resolvedLatestRunId } : null);
  if (isLocalAccount) {
    setLocalRunsContext({ accountId: requestedAccount, runs: completedRuns });
    setLatestRunDate(latestRun?.run_date || null);
  } else {
    setLocalRunsContext(null);
  }
  await fetchRetailVisibilitySummary(resolvedLatestRunId, requestedAccount);
}

  // ----------------------------
  // FETCH METRICS (SCORECARDS)
  // ----------------------------
  async function fetchDataset() {
    if (!account || !latestRunId) return;

    const comparisonRunIds = [latestRunId, previousRunId].filter((runId): runId is string => Boolean(runId));
    const { data: promptRows, error } = await supabase
      .from("prompt_results")
      .select("run_id, brand_mentions, visibility_signal, prompt_type, status")
      .eq("account_id", account)
      .in("run_id", comparisonRunIds)
      .eq("prompt_type", "organic")
      .eq("status", "completed")
      .neq("visibility_signal", "error")
      .limit(2000);

    if (error) {
      console.warn("[Dashboard] Lightweight visibility metrics query error", error);
      setMetricsStatus("empty");
      setPreviousMetrics(null);
      return;
    }

    const currentMetrics = calculateDashboardMetrics(
      (promptRows || []).filter((row: any) => row.run_id === latestRunId),
      customerBrand
    );
    const priorMetrics = previousRunId
      ? calculateDashboardMetrics(
          (promptRows || []).filter((row: any) => row.run_id === previousRunId),
          customerBrand
        )
      : null;

    if (!currentMetrics) {
      setMetricsStatus("empty");
      setMetrics({
        visibility: 0,
        organicShare: 0,
        recommendation: 0,
        rank: 0,
        totalCompetitors: 0,
        authority: 0
      });
      setPreviousMetrics(null);
      return;
    }

    setMetrics(currentMetrics);
    setPreviousMetrics(priorMetrics);

    setMetricsStatus("ready");
  }

  // ----------------------------
  // FETCH PROMPT DATA (KEEP)
  // ----------------------------
  async function fetchPromptDataset() {
    const { data, error } = await supabase
      .from("prompt_results")
      .select("prompt_id, run_id, category, prompt_score, visibility_signal, brand_mentions, llm_response_text, prompt_type")
      .eq("account_id", account)
      .eq("run_id", latestRunId)
      .limit(1000);

    if (error) {
      console.error("Prompt error:", error);
      setPromptDataset([]);
      return;
    }

    setPromptDataset(data || []);
  }

  // ----------------------------
  // RENDER
  // ----------------------------

  if (dashboardStatus !== "ready") {
  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh" }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "40px 16px" }}>
        <div style={{ display: "flex", justifyContent: "flex-end", gap:10, marginBottom: 16 }}>

  <button
    onClick={() => supabase.auth.signOut()}
    style={{
      border: "1px solid #d1d5db",
      background: "#ffffff",
      color: "#374151",
      borderRadius: 999,
      padding: "8px 14px",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer"
    }}
  >
    Sign out
  </button>

</div>
        <div
          style={{
            background: "white",
            borderRadius: 10,
            padding: 20,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            color: colors.textSecondary,
            fontSize: 14
          }}
        >
          {dashboardStatus === "loading"
            ? "Loading dashboard…"
            : dashboardMessage || "The dashboard is not available right now."}
        </div>
      </div>
    </div>
  );
}


  return (
  <>
  <style>{`
    @media (max-width: 640px) {
      .dashboard-export-root { padding: 20px 12px 32px !important; }
      .dashboard-topbar { align-items: stretch !important; flex-direction: column; gap: 16px !important; }
      .dashboard-brand { flex-wrap: wrap; }
      .dashboard-brand-divider { display: none !important; }
      .dashboard-brand-copy { flex-basis: 100%; }
      .dashboard-logo { width: 140px !important; }
      .dashboard-header-actions { justify-content: flex-start !important; flex-wrap: wrap; }
    }
  `}</style>
  <div style={{ background: "#f8fafc", minHeight: "100vh" }}>
    <div
    ref={dashboardExportRef}
    className="dashboard-export-root"
    style={{
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "56px 20px 40px"
      }}
    >
<div
  className="dashboard-topbar"
  style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 24,
    marginBottom: 28
  }}
>
  <div
    className="dashboard-brand"
    style={{
      display: "flex",
      alignItems: "center",
      gap: 16
    }}
  >
    <img
  src="/toren-logo-navy-tm.svg"
  alt="Toren"
  className="dashboard-logo"
  style={{
    width: 170,
    height: "auto",
    display: "block",
    flexShrink: 0
  }}
/>

    <div
      className="dashboard-brand-divider"
      style={{
        width: 1,
        height: 34,
        background: "#d1d5db"
      }}
    />

    <div className="dashboard-brand-copy">
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "#1f2937"
        }}
      >
        AI Intelligence Platform
      </div>
      <div
        style={{
          marginTop: 3,
          fontSize: 13,
          color: "#6b7280"
        }}
      >
        Market visibility, recommendations, and opportunity signals
      </div>
    </div>
  </div>
<div className="dashboard-header-actions" style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
<button
  type="button"
  className="no-print"
  onClick={handleExportPdf}
  disabled={isExportingPdf}
  aria-busy={isExportingPdf}
  style={{
    border: "1px solid #d1d5db",
    background: "#ffffff",
    color: "#374151",
    borderRadius: 999,
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 600,
    cursor: isExportingPdf ? "wait" : "pointer",
    opacity: isExportingPdf ? 0.7 : 1
  }}
>
  {isExportingPdf ? "Generating…" : "Export PDF"}
</button>

  <button
    onClick={() => supabase.auth.signOut()}
    style={{
      border: "1px solid #d1d5db",
      background: "#ffffff",
      color: "#374151",
      borderRadius: 999,
      padding: "8px 14px",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer"
    }}
  >
    Sign out
  </button>
</div>
</div>

      {/* ACCOUNT SELECT */}
    <div
  style={{
    marginTop: 18,
    color: "#6b7280",
    fontSize: 14
  }}
>
  <span>Viewing: </span>

  {canSwitchAccounts ? (
    <select
      value={account || ""}
      data-pdf-static-label="true"
      onChange={(event) => setAccount(event.target.value)}
      style={{
        marginLeft: 6,
        padding: "6px 10px",
        borderRadius: 10,
        border: `1px solid ${colors.border}`,
        background: "#ffffff",
        color: "#1f2937",
        fontSize: 14,
        fontWeight: 700,
        minWidth: 260,
        cursor: "pointer"
      }}
    >
      {accounts.map((item: any) => (
        <option key={item.account_id} value={item.account_id}>
          {getAccountDisplayName(item)}
        </option>
      ))}
    </select>
  ) : (
    <strong style={{ color: "#1f2937" }}>
      {getAccountDisplayName(selectedAccount)}
    </strong>
  )}

  {selectedAccount?.status === "demo" && (
    <span
      style={{
        marginLeft: 8,
        padding: "2px 8px",
        borderRadius: 999,
        background: "#eef2ff",
        color: "#3730a3",
        fontSize: 12,
        fontWeight: 700
      }}
    >
      Demo
    </span>
  )}
</div>

{isLocalAccount && (
  <LocalReportingPeriodSelector
    runs={activeLocalRuns}
    selectedRunId={activeLocalRunId}
    loading={!localRunsContext || localRunsContext.accountId !== account}
    onChange={handleLocalReportingPeriodChange}
  />
)}

<div
  style={{
    marginTop: 8,
    fontSize: 12,
    color: (isLocalAccount ? selectedLocalRun?.completed_at : latestRunDate) ? "#6b7280" : "#92400e"
  }}
>
  {activeRunFreshnessLabel}
</div>

{selectedAccount && (
  <div data-testid="dashboard-narrative-flow">
    <section data-testid="dashboard-interpretation-layer" style={{ marginTop: 18 }}>
      {!isLocalAccount && account ? (
        <AIAnalyst
          account={account}
          isPro={!!selectedAccount?.analyst_pro_enabled}
          customerBrand={customerBrand}
          promptDataset={promptDataset}
          metrics={metrics}
          runId={latestRunId}
        />
      ) : null}
    </section>

    {!isLocalAccount && (
      <section data-testid="dashboard-key-signals" aria-labelledby="dashboard-key-signals-heading" style={{ marginTop: 18 }}>
        <div id="dashboard-key-signals-heading" style={{ color: "#64748b", fontSize: 12, fontWeight: 900, letterSpacing: "0.06em", textTransform: "uppercase" }}>Key signals</div>
        {metricsStatus !== "ready" && (
          <div style={{ marginTop: 8, color: colors.textSecondary, fontSize: 13 }}>
            {metricsStatus === "error" ? "Scorecard metrics could not load right now." : "Not enough scorecard data available yet."}
          </div>
        )}
        <div data-testid="oe-core-visibility-metrics" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10, marginTop: 9 }}>
          <Scorecard compact title={<span>AI VISIBILITY<Tooltip text="How often your brand appears in AI-generated responses." /></span>} value={metricsStatus === "ready" ? formatPercentage(metrics.visibility) : "—"} comparison={percentageComparison(metrics.visibility, previousMetrics?.visibility ?? null, "Visibility")} />
          <Scorecard compact title={<span>RECOMMENDATION STRENGTH<Tooltip text="How frequently your brand is recommended when AI suggests options." /></span>} value={metricsStatus === "ready" && metrics.recommendation > 0 ? formatPercentage(metrics.recommendation) : "—"} comparison={percentageComparison(metrics.recommendation, previousMetrics?.recommendation ?? null, "Recommendation strength")} />
          <Scorecard compact title={<span>COMPETITIVE POSITION<Tooltip text="Your position by mention frequency across completed organic AI responses." /></span>} value={metricsStatus === "ready" ? metrics.rank > 0 ? `#${metrics.rank} of ${metrics.totalCompetitors}` : `Outside top ${metrics.totalCompetitors}` : "—"} comparison={positionComparison(metrics, previousMetrics)} />
          <Scorecard compact title={<span>AI AUTHORITY<Tooltip text="How strongly AI endorses your brand when it appears." /></span>} value={metricsStatus === "ready" ? formatPercentage(metrics.authority) : "—"} comparison={percentageComparison(metrics.authority, previousMetrics?.authority ?? null, "Endorsement strength")} />
        </div>
        <div data-testid="oe-core-visibility-summary" style={{ marginTop: 10, padding: "11px 13px", borderRadius: 10, background: "#ffffff", border: "1px solid #e5e7eb", fontSize: 13, color: "#475569", lineHeight: 1.45 }}>
          <strong style={{ color: "#111827" }}>AI Scorecard Summary. </strong>
          {metricsStatus !== "ready" ? "Toren is preparing the latest AI performance summary for this account." : <>{metrics.visibility < 10 ? "Visibility is limited across AI responses." : "Visibility is consistent across AI responses."} {metrics.recommendation < 20 ? "Recommendation strength remains the clearest gap." : "Recommendation strength is established."}</>}
        </div>
      </section>
    )}

    <section data-testid="dashboard-core-evidence" style={{ marginTop: 22 }}>
      {isRetailVisibilityEnabled(account) && (
        <AIRetailVisibility
          status={retailVisibilitySummary.status}
          score={retailVisibilitySummary.score}
          headline={retailVisibilitySummary.headline}
          narrative={retailVisibilitySummary.narrative}
          recommendedAction={retailVisibilitySummary.recommendedAction}
          customerBrandMentionRate={retailVisibilitySummary.customerBrandMentionRate}
          priorityRetailerMentionRate={retailVisibilitySummary.priorityRetailerMentionRate}
          ownedChannelMentionRate={retailVisibilitySummary.ownedChannelMentionRate}
          priorityChannels={retailVisibilitySummary.priorityChannels}
          channelAlternatives={retailVisibilitySummary.channelAlternatives}
          topCompetitors={retailVisibilitySummary.topCompetitors}
          productInsights={retailVisibilitySummary.productInsights}
        />
      )}
      {isLocalAccount && account && (
        <div style={{ marginTop: 18 }}>
          {/* LocalVisibility renders the analyst, customer radar, then the existing Local leaderboard. */}
          <div data-testid="oe-local-visibility-summary"><LocalVisibility accountId={account} runId={activeLocalRunId} isAdminView={canRunAnalysis} historyComparison={activeLocalHistoryComparison} runFreshnessLabel={activeRunFreshnessLabel} /></div>
          <section data-testid="local-scorecards-history" aria-labelledby="local-scorecards-heading" style={{ marginTop: 18, borderTop: `1px solid ${colors.border}`, paddingTop: 18 }}>
            <div style={{ marginBottom: 12 }}>
              <p style={{ margin: 0, color: colors.textSecondary, fontSize: 11, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase" }}>Supporting evidence</p>
              <h2 id="local-scorecards-heading" style={{ margin: "4px 0 0", color: colors.textPrimary, fontSize: 20, lineHeight: 1.25 }}>Scorecards &amp; historical performance</h2>
              <p style={{ margin: "5px 0 0", color: colors.textSecondary, fontSize: 13 }}>Completed-run history and model consensus for this account.</p>
            </div>
            <div data-testid="oe-local-visibility-history"><LocalHistoricalVisibility key={account} account={account} customerBrand={customerBrand} selectedRunId={activeLocalRunId} onComparisonUpdate={handleLocalHistoryComparisonUpdate} /></div>
            <article data-testid="oe-local-consensus-visible" style={{ marginTop: 16 }}><AIConsensus account={account} mode="local" customerBrand={customerBrand} runId={activeLocalRunId} /></article>
          </section>
        </div>
      )}
      {!isLocalAccount && account && (
        <>
          <div data-testid="oe-competitive-visibility-history" style={{ marginTop: 18 }}>
            <CompetitiveHistoricalVisibility account={account} customerBrand={customerBrand} canReviewEntityGovernance={canReviewEntityGovernance} />
          </div>
          {/* MODULES */}
          <div data-testid="oe-competitive-evidence-modules" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(520px, 1fr))", columnGap: 18, rowGap: 16, marginTop: 24 }}>
            {modules
              .filter(([key]) => key !== "analyst" && key !== "opportunityEngine")
              .map(([key]: any) => {
                const Component = componentMap[key];
                if (!Component) return null;
                const isFullWidth = key === "visibilityBreakdown";
                const componentProps = { account, mode: accountMode, customerBrand, runId: latestRunId };
                return <div key={key} data-testid={key === "aiConsensus" ? "oe-competitive-consensus-visible" : key === "marketMap" ? "oe-market-map-visible" : key === "leaderboard" ? "oe-leaderboard-visible" : undefined} style={{ gridColumn: isFullWidth ? "1 / -1" : "auto", transition: "all 0.3s ease" }}><Component {...componentProps} /></div>;
              })}
          </div>
        </>
      )}
    </section>

    <CaiGenerationEntitlementBoundary value={isCaiGenerationEnabled(selectedAccount)}>
    <section data-testid="oe-workspace" aria-labelledby="oe-workspace-heading" style={{ marginTop: 14, borderTop: "1px solid #dbeafe", paddingTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div>
          <div style={{ color: "#2563eb", fontSize: 12, fontWeight: 900, letterSpacing: "0.06em", textTransform: "uppercase" }}>Opportunity Engine workspace</div>
          <h2 id="oe-workspace-heading" style={{ margin: "5px 0 0", color: "#172554", fontSize: 20 }}>Turn intelligence into meaningful improvements.</h2>
        </div>
      </div>
      <div role="tablist" aria-label="Opportunity Engine workspace" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
        {oeWorkspaceTabs.map((tab) => <OeWorkspaceTabButton key={tab.id} id={tab.id} label={tab.id === "competitive" && isLocalAccount ? "Local" : tab.label} active={oeWorkspaceTab === tab.id} onSelect={setOeWorkspaceTab} />)}
      </div>

      {oeWorkspaceTab === "assets" && (
        <div id="oe-workspace-panel-assets" role="tabpanel" aria-labelledby="oe-workspace-tab-assets" data-testid="oe-workspace-assets" style={{ marginTop: 12 }}>
          <CustomerCurrentAssetIntelligence account={selectedAccount} presentation="brief" />
          {canViewOpportunityEngine && account && <CaiCategoryWorkspace accountId={account} runId={latestRunId} accountName={customerBrand || getAccountDisplayName(selectedAccount)} accountType={accountType} isPlatformAdmin={isPlatformAdminForCai} category="asset" />}
        </div>
      )}
      {oeWorkspaceTab === "entity" && (
        <div id="oe-workspace-panel-entity" role="tabpanel" aria-labelledby="oe-workspace-tab-entity" data-testid="oe-workspace-entity" style={{ marginTop: 12 }}>
          <EntityFoundationCard accountName={customerBrand || getAccountDisplayName(selectedAccount)} />
          {canViewOpportunityEngine && account && <CaiCategoryWorkspace accountId={account} runId={latestRunId} accountName={customerBrand || getAccountDisplayName(selectedAccount)} accountType={accountType} category="entity" />}
        </div>
      )}
      {oeWorkspaceTab === "competitive" && (
        <div id="oe-workspace-panel-competitive" role="tabpanel" aria-labelledby="oe-workspace-tab-competitive" data-testid="oe-workspace-competitive" style={{ marginTop: 12 }}>
          {canViewOpportunityEngine && account && (isLocalAccount
            ? <LocalOpportunityEngine accountId={account} presentation="focused" initialVisibleCount={3} />
            : <OpportunityEngine account={account} mode={accountMode} accountType={accountType} entitlementOverride={selectedAccount?.local_config?.contentBriefEntitlement || selectedAccount?.local_config?.content_brief_entitlement} customerBrand={customerBrand} runId={latestRunId} presentation="focused" initialVisibleCount={5} />)}
        </div>
      )}
      {!canViewOpportunityEngine && <div data-testid="oe-workspace-opportunity-guidance" style={{ marginTop: 14, border: "1px solid #dbeafe", borderRadius: 12, padding: 14, background: "#ffffff", color: "#475569" }}>Opportunity guidance is not available for this account view yet.</div>}
    </section>
    </CaiGenerationEntitlementBoundary>
  </div>
)}
{canSwitchAccounts && (
  <details
    style={{
      marginTop: 40,
      padding: "14px 16px",
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      background: "#ffffff",
      color: "#6b7280",
      fontSize: 13
    }}
  >
    <summary
      style={{
        cursor: "pointer",
        fontWeight: 700,
        color: "#374151"
      }}
    >
      Account controls
    </summary>

    <div
      style={{
        marginTop: 14,
        display: "flex",
        alignItems: "flex-end",
        gap: 12,
        flexWrap: "wrap"
      }}
    >
      <div>
        <label
          style={{
            display: "block",
            marginBottom: 6,
            fontSize: 12,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: "#6b7280"
          }}
        >
          Switch account
        </label>

        <select
          value={account || ""}
          onChange={(e) => setAccount(e.target.value)}
          style={{
            minWidth: 240,
            border: "1px solid #d1d5db",
            borderRadius: 8,
            padding: "8px 10px",
            background: "#ffffff",
            color: "#1f2937"
          }}
        >
          {accounts.map((a: any) => (
            <option key={a.account_id} value={a.account_id}>
              {getAccountDisplayName(a)}
              {a.status === "demo" ? " — Demo" : ""}
            </option>
          ))}
        </select>
      </div>

     {canRunAnalysis && (
  <>
    <button
      disabled={running || !account}
      onClick={async () => {
        try {
          if (!account) {
            alert("Missing account. The production run was not started.");
            console.error("[Dashboard] Missing production run-analysis inputs", {
              account
            });
            return;
          }

          const accountName =
            selectedAccount ? getAccountDisplayName(selectedAccount) : "this account";

          const confirmRun = window.confirm(
            `Run production analysis for ${accountName}?\n\nAccount ID: ${account}`
          );

          if (!confirmRun) return;

          setRunning(true);

          const res = await postRunAnalysis({
            account_id: account,
            run_label: "manual-admin-run",
            model_profile: "production_competitive"
          }, { auth: supabase.auth });

          const responseText = await res.text();

          let data = null;
          try {
            data = responseText ? JSON.parse(responseText) : null;
          } catch {
            data = responseText;
          }

          if (!res.ok) {
            console.error("❌ Run request returned non-OK status:", {
              status: res.status,
              statusText: res.statusText,
              data
            });

            alert(
              `Run request returned ${res.status}. The run was not started. Check backend trigger logs.`
            );

            return;
          }

          console.log("✅ Run response:", {
            status: res.status,
            data
          });

          alert(
            `Production run started successfully for ${accountName}.\n\nRun ID: ${data?.run_id || "created"}`
          );
        } catch (error) {
          console.error("❌ Failed to start run:", error);
          alert("Failed to start run. Check console/logs.");
        } finally {
          setRunning(false);
        }
      }}
      style={{
        border: "1px solid #d1d5db",
        background: running ? "#f3f4f6" : "#ffffff",
        color: "#374151",
        borderRadius: 999,
        padding: "9px 14px",
        fontSize: 13,
        fontWeight: 700,
        cursor: running ? "not-allowed" : "pointer"
      }}
    >
      {running ? "Starting..." : "🚀 Run Analysis"}
    </button>

    <button
      disabled={running || !account}
      onClick={async () => {
        try {
          if (!account) {
            alert("Missing account. The demo run was not started.");
            console.error("[Dashboard] Missing demo run-analysis inputs", {
              account
            });
            return;
          }

          const accountName =
            selectedAccount ? getAccountDisplayName(selectedAccount) : "this account";

          const confirmRun = window.confirm(
            `Run DEMO analysis for ${accountName}?\n\nAccount ID: ${account}\n\nThis uses the demo model profile.`
          );

          if (!confirmRun) return;

          setRunning(true);

          const demoPayload = {
            account_id: account,
            run_label: "demo-admin-run",
            model_profile: "demo_competitive"
          };

          console.log("[Dashboard] Demo run-analysis payload", demoPayload);

          const res = await postRunAnalysis(demoPayload, { auth: supabase.auth });

          const responseText = await res.text();

          let data = null;
          try {
            data = responseText ? JSON.parse(responseText) : null;
          } catch {
            data = responseText;
          }

          if (!res.ok) {
            console.error("❌ Demo run request returned non-OK status:", {
              status: res.status,
              statusText: res.statusText,
              data
            });

            alert(
              `Demo run request returned ${res.status}. The run was not started. Check backend trigger logs.`
            );

            return;
          }

          console.log("✅ Demo run response:", {
            status: res.status,
            data
          });

          alert(
            `Demo run started successfully for ${accountName}.\n\nRun ID: ${data?.run_id || "created"}`
          );
        } catch (error) {
          console.error("❌ Failed to start demo run:", error);
          alert("Failed to start demo run. Check console/logs.");
        } finally {
          setRunning(false);
        }
      }}
      style={{
        border: "1px solid #bae6fd",
        background: running ? "#f3f4f6" : "#f0f9ff",
        color: "#075985",
        borderRadius: 999,
        padding: "9px 14px",
        fontSize: 13,
        fontWeight: 700,
        cursor: running ? "not-allowed" : "pointer"
      }}
    >
      {running ? "Starting..." : "⚡ Run Demo Analysis"}
    </button>
  </>
 )}
     </div>
  </details>
)}
      <footer
        style={{
          marginTop: 36,
          padding: "18px 0 4px",
          borderTop: "1px solid #e5e7eb",
          color: "#64748b",
          fontSize: 12,
          lineHeight: 1.5,
          textAlign: "center"
        }}
      >
        © 2026 Toren. All rights reserved. For questions or support, contact{" "}
        <a
          href="mailto:support@torenindex.com"
          style={{
            color: "#155e75",
            fontWeight: 700,
            textDecoration: "none"
          }}
        >
          support@torenindex.com
        </a>
        .
      </footer>
    </div>
  </div>
  </>
);
}
